import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    projectId: Number,
    currentTabId: Number,
    phases: Array,
    confirmDeleteNode: String,
    promptNewTab: String,
    promptRenameTab: String,
    confirmDeleteTab: String,
    defaultNodeTitle: String,
    statusPending: String,
    statusInProgress: String,
    statusDone: String,
    saveError: String,
    priorityLabel: String
  }

  static targets = [
    "filterDropdown", "filterBadge", "filterAll", "filterCheckbox",
    "table", "column",
    "colVisMgr", "colVisPanel", "colVisCheckbox", "colVisAll"
  ]

  connect() {
    this.handleKlavyAgentApply = this.handleKlavyAgentApply.bind(this)
    this.handleColumnResize = this.handleColumnResize.bind(this)
    this.finishColumnResize = this.finishColumnResize.bind(this)
    // 외부 클릭 핸들러 참조 보관 (disconnect에서 정리)
    this._filterOutsideHandler = null
    this._colVisOutsideHandler = null
    this._tabCtxOutsideHandlers = new Map()
    window.addEventListener("ia-designer:apply-actions", this.handleKlavyAgentApply)
    this._ensureFilterInit()
    this._updateTableMinWidth()
    this.restoreColumnWidths()
    this._restoreColVisibility()
  }

  _ensureFilterInit() {
    if (!this.hasFilterCheckboxTarget) return
    this.filterCheckboxTargets.forEach(cb => { cb.checked = true })
    if (this.hasFilterAllTarget) this.filterAllTarget.checked = true
    if (this.hasFilterBadgeTarget) this.filterBadgeTarget.style.display = "none"
    this.element.querySelectorAll("tr[data-depth]").forEach(row => {
      row.style.display = ""
    })
  }

  disconnect() {
    window.removeEventListener("ia-designer:apply-actions", this.handleKlavyAgentApply)
    this.teardownColumnResize()
    // 외부 클릭 리스너 정리
    if (this._filterOutsideHandler) {
      document.removeEventListener("click", this._filterOutsideHandler)
      this._filterOutsideHandler = null
    }
    if (this._colVisOutsideHandler) {
      document.removeEventListener("click", this._colVisOutsideHandler)
      this._colVisOutsideHandler = null
    }
    if (this._tabCtxOutsideHandlers) {
      this._tabCtxOutsideHandlers.forEach((handler) => {
        document.removeEventListener("click", handler)
      })
      this._tabCtxOutsideHandlers.clear()
    }
    const styleEl = document.getElementById('ia-col-vis-css')
    if (styleEl) styleEl.remove()
  }

  // ---------- Filter ----------

  toggleFilter(event) {
    event.stopPropagation()
    const dropdown = this.filterDropdownTarget
    const isOpen = dropdown.style.display !== "none"
    dropdown.style.display = isOpen ? "none" : "block"

    if (!isOpen) {
      if (this._filterOutsideHandler) {
        document.removeEventListener("click", this._filterOutsideHandler)
      }
      this._filterOutsideHandler = (e) => {
        if (!this.element.querySelector(".ia-filter").contains(e.target)) {
          dropdown.style.display = "none"
          document.removeEventListener("click", this._filterOutsideHandler)
          this._filterOutsideHandler = null
        }
      }
      setTimeout(() => document.addEventListener("click", this._filterOutsideHandler), 0)
    }
  }

  toggleAll(event) {
    const allChecked = event.currentTarget.checked
    this.filterCheckboxTargets.forEach(cb => { cb.checked = allChecked })
    this._applyFilterState()
  }

  applyFilter() {
    const checked = this.filterCheckboxTargets.filter(c => c.checked)
    const allChecked = checked.length === this.filterCheckboxTargets.length
    this.filterAllTarget.checked = allChecked
    this.filterAllTarget.indeterminate = !allChecked && checked.length > 0
    this._applyFilterState()
  }

  _applyFilterState() {
    const checked = new Set(this.filterCheckboxTargets.filter(c => c.checked).map(c => c.value))
    const allChecked = checked.size === this.filterCheckboxTargets.length

    // Filter list view rows
    this.element.querySelectorAll("tr[data-depth]").forEach(row => {
      row.style.display = checked.has(row.dataset.depth) ? "" : "none"
    })

    const badge = this.filterBadgeTarget
    if (allChecked) {
      badge.style.display = "none"
    } else {
      badge.textContent = checked.size
      badge.style.display = ""
    }
  }

  // ---------- Toggle expand/collapse ----------

  toggle(event) {
    event.stopPropagation()
    const row = event.currentTarget.closest("[data-node-id]")
    if (!row) return
    const nodeId = row.dataset.nodeId
    const expanded = row.dataset.expanded === "true"

    row.dataset.expanded = expanded ? "false" : "true"
    this.updateToggleIcon(row, !expanded)
    this.toggleChildren(nodeId, !expanded)
  }

  updateToggleIcon(row, expanded) {
    const icon = row.querySelector("[data-toggle-icon]")
    if (icon) icon.innerHTML = expanded ? "&#9660;" : "&#9654;"
  }

  toggleChildren(parentId, visible) {
    const children = this.element.querySelectorAll(`[data-parent-id="${parentId}"]`)
    children.forEach(child => {
      child.style.display = visible ? "" : "none"
      if (!visible) {
        child.dataset.expanded = "false"
        this.updateToggleIcon(child, false)
        // chain 안의 모든 노드 ID에 대해 자식도 함께 숨김
        const chainIds = (child.dataset.chainNodeIds || child.dataset.nodeId).split(",")
        chainIds.forEach(id => this.toggleChildren(id.trim(), false))
      }
    })
  }

  // ---------- Inline cell editing ----------

  editCell(event) {
    const cell = event.currentTarget
    if (cell.querySelector("input, select")) return

    const field = cell.dataset.field
    const row = cell.closest("[data-node-id]")
    if (!row) return
    // chain 구조에서 각 <td>에 data-cell-node-id가 있으면 그 노드 사용
    const nodeId = cell.dataset.cellNodeId || row.dataset.nodeId
    const currentValue = cell.dataset.value || cell.textContent.trim()

    this.showTextInput(cell, nodeId, field, currentValue)
  }

  showTextInput(cell, nodeId, field, currentValue) {
    const input = document.createElement("input")
    input.type = "text"
    input.value = currentValue
    input.style.cssText = "width:100%;box-sizing:border-box;border:1px solid #6366F1;padding:5px 8px;font-size:inherit;font-family:inherit;outline:none;background:#fff;"

    const originalContent = cell.innerHTML
    cell.innerHTML = ""
    cell.appendChild(input)
    input.focus()
    input.select()

    let saved = false
    const save = () => {
      if (saved) return
      saved = true
      const newValue = input.value.trim()
      cell.innerHTML = originalContent
      if (newValue && newValue !== currentValue) {
        cell.dataset.value = newValue
        // Update displayed text — find the text span or use cell directly
        const textSpan = cell.querySelector("[data-field-text]")
        if (textSpan) {
          textSpan.textContent = newValue
        } else {
          cell.textContent = newValue
        }
        this.updateNode(nodeId, { [field]: newValue })
      }
    }

    input.addEventListener("blur", save)
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); save() }
      if (e.key === "Escape") { saved = true; cell.innerHTML = originalContent }
    })
  }

  // ---------- Node CRUD ----------

  async updateNode(nodeId, fields) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(`/ia/projects/${this.projectIdValue}/nodes/${nodeId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ node: fields })
      })
      if (!res.ok) {
        console.error("[IaTable] updateNode 서버 오류:", res.status)
        alert(this.saveErrorValue || "저장 중 오류가 발생했습니다. 페이지를 새로고침해 주세요.")
        window.location.reload()
      }
    } catch (err) {
      console.error("[IaTable] updateNode failed:", err)
      alert(this.saveErrorValue || "저장 중 오류가 발생했습니다. 페이지를 새로고침해 주세요.")
      window.location.reload()
    }
  }

  async addNode(event) {
    event.stopPropagation()
    const parentId = event.currentTarget.dataset.parentId || null
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(`/ia/projects/${this.projectIdValue}/nodes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({
          node: {
            parent_id: parentId || null,
            node_type: "page",
            title: this.defaultNodeTitleValue || "새 항목",
            path_key: `node_${Date.now()}`,
            purpose: "",
            ia_tab_id: this.currentTabIdValue
          }
        })
      })
      if (res.ok) window.location.reload()
    } catch (err) {
      console.error("[IaTable] addNode failed:", err)
    }
  }

  async moveNode(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const nodeId = btn.dataset.nodeId
    const direction = btn.dataset.direction
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(`/ia/projects/${this.projectIdValue}/nodes/${nodeId}/move`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ direction })
      })
      if (res.ok) window.location.reload()
    } catch (err) {
      console.error("[IaTable] moveNode failed:", err)
    }
  }

  async deleteNode(event) {
    event.stopPropagation()
    const nodeId = event.currentTarget.dataset.nodeId
    if (!confirm(this.confirmDeleteNodeValue || "이 항목을 삭제할까요?")) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(`/ia/projects/${this.projectIdValue}/nodes/${nodeId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": token }
      })
      window.location.reload()
    } catch (err) {
      console.error("[IaTable] deleteNode failed:", err)
    }
  }

  // ---------- KlavyAgent bridge ----------

  async handleKlavyAgentApply(event) {
    const { data, resolve, reject } = event.detail || {}
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(`/ia/projects/${this.projectIdValue}/klavy_agent_actions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ ...data, tab_id: this.currentTabIdValue })
      })
      const result = await res.json()
      if (resolve) resolve(result)
      window.location.reload()
    } catch (err) {
      console.error("[IaTable] klavy_agent_actions failed:", err)
      if (reject) reject(err)
    }
  }

  startColumnResize(event) {
    if (!this.hasTableTarget) return
    if (window.matchMedia("(max-width: 767px)").matches) return

    event.preventDefault()
    event.stopPropagation()

    const handle = event.currentTarget
    const columnIndex = Number(handle.dataset.columnIndex)
    if (!Number.isInteger(columnIndex) || columnIndex < 0 || columnIndex >= this.columnTargets.length - 1) return

    const tableWidth = this.tableTarget.getBoundingClientRect().width
    if (!tableWidth) return

    this.columnWidths = this.getColumnWidths()
    this.resizeState = {
      columnIndex,
      startX: event.clientX,
      tableWidth,
      startLeftPx: (this.columnWidths[columnIndex] / 100) * tableWidth,
      startRightPx: (this.columnWidths[columnIndex + 1] / 100) * tableWidth
    }

    this.tableTarget.classList.add("ia-table--resizing")
    window.addEventListener("pointermove", this.handleColumnResize)
    window.addEventListener("pointerup", this.finishColumnResize)
    window.addEventListener("pointercancel", this.finishColumnResize)
  }

  handleColumnResize(event) {
    if (!this.resizeState) return

    const { columnIndex, startX, tableWidth, startLeftPx, startRightPx } = this.resizeState
    const minLeftPx = this.minimumWidthFor(columnIndex)
    const minRightPx = this.minimumWidthFor(columnIndex + 1)
    const totalPairPx = startLeftPx + startRightPx
    const rawLeftPx = startLeftPx + (event.clientX - startX)
    const nextLeftPx = Math.min(Math.max(rawLeftPx, minLeftPx), totalPairPx - minRightPx)
    const nextRightPx = totalPairPx - nextLeftPx

    this.columnWidths[columnIndex] = (nextLeftPx / tableWidth) * 100
    this.columnWidths[columnIndex + 1] = (nextRightPx / tableWidth) * 100

    this.applyColumnWidths(this.columnWidths)
  }

  finishColumnResize() {
    if (!this.resizeState) return

    this.saveColumnWidths(this.columnWidths)
    this.teardownColumnResize()
  }

  teardownColumnResize() {
    if (this.hasTableTarget) {
      this.tableTarget.classList.remove("ia-table--resizing")
    }

    window.removeEventListener("pointermove", this.handleColumnResize)
    window.removeEventListener("pointerup", this.finishColumnResize)
    window.removeEventListener("pointercancel", this.finishColumnResize)
    this.resizeState = null
  }

  restoreColumnWidths() {
    if (!this.hasColumnTarget) return

    const widths = this.loadColumnWidths() || this.defaultColumnWidths()
    this.columnWidths = widths
    this.applyColumnWidths(widths)
  }

  applyColumnWidths(widths) {
    if (!this.hasColumnTarget) return

    this.columnTargets.forEach((column, index) => {
      column.style.width = `${widths[index]}%`
    })
  }

  getColumnWidths() {
    return this.columnTargets.map((column, index) => {
      const width = parseFloat(column.style.width || "")
      return Number.isFinite(width) ? width : this.defaultColumnWidths()[index]
    })
  }

  defaultColumnWidths() {
    // Base column widths in px: 9 fixed columns (depth×5 + 페이지수 + URL + 우선순위 + 메모)
    const basePx = [115, 115, 115, 115, 115, 86, 160, 104, 220]
    const phaseSubPx = [90, 90, 90, 70] // 담당자/시작일/완료일/완료
    const phaseCount = (this.phasesValue || []).length

    if (phaseCount === 0) {
      // No phases: convert to percentages of total (sum = 100%)
      const total = basePx.reduce((s, w) => s + w, 0)
      return basePx.map(w => (w / total) * 100)
    }

    // With phases: keep base cols unchanged, append phase sub-cols
    // All widths as percentages of the fixed total pixel width
    const phaseSubTotal = phaseSubPx.reduce((s, w) => s + w, 0)
    const totalPx = basePx.reduce((s, w) => s + w, 0) + phaseCount * phaseSubTotal
    const allPx = [...basePx, ...Array.from({ length: phaseCount }, () => phaseSubPx).flat()]
    return allPx.map(w => (w / totalPx) * 100)
  }

  minimumWidthFor(index) {
    // Minimum widths in pixels (used by resize logic)
    const mins = [72, 72, 72, 72, 72, 70, 60, 88, 100]
    if (index < mins.length) return mins[index]
    const phaseSubIndex = (index - 9) % 4
    return [50, 55, 55, 45][phaseSubIndex] || 45
  }

  _updateTableMinWidth() {
    if (!this.hasTableTarget) return
    const phaseCount = (this.phasesValue || []).length
    if (phaseCount > 0) {
      // Base cols total: 115*5 + 86 + 160 + 104 + 220 = 1145px
      // Phase sub-cols per phase: 90 + 90 + 90 + 70 = 340px
      const totalPx = 1145 + phaseCount * 340
      this.tableTarget.style.width = `${totalPx}px`
      this.tableTarget.style.minWidth = `${totalPx}px`
    } else {
      this.tableTarget.style.minWidth = ''
      this.tableTarget.style.width = '100%'
    }
  }

  storageKey() {
    const phaseCount = (this.phasesValue || []).length
    return `ia-table-widths:${this.projectIdValue}:${this.currentTabIdValue}:p${phaseCount}`
  }

  // ---------- Column visibility ----------

  toggleColVisPanel(event) {
    event.stopPropagation()
    const panel = this.colVisPanelTarget
    const isOpen = panel.style.display !== 'none'
    panel.style.display = isOpen ? 'none' : 'block'
    if (!isOpen) {
      if (this._colVisOutsideHandler) {
        document.removeEventListener('click', this._colVisOutsideHandler)
      }
      this._colVisOutsideHandler = (e) => {
        if (!this.colVisMgrTarget.contains(e.target)) {
          panel.style.display = 'none'
          document.removeEventListener('click', this._colVisOutsideHandler)
          this._colVisOutsideHandler = null
        }
      }
      setTimeout(() => document.addEventListener('click', this._colVisOutsideHandler), 0)
    }
  }

  applyColVis() {
    const hidden = new Set(
      this.colVisCheckboxTargets.filter(cb => !cb.checked).map(cb => cb.value)
    )
    this._applyColVisibility(hidden)
    this._saveColVisibility([...hidden])
    const anyUnchecked = this.colVisCheckboxTargets.some(cb => !cb.checked)
    const anyChecked = this.colVisCheckboxTargets.some(cb => cb.checked)
    this.colVisAllTarget.checked = !anyUnchecked
    this.colVisAllTarget.indeterminate = anyUnchecked && anyChecked
  }

  toggleAllColVis(event) {
    const checked = event.currentTarget.checked
    this.colVisCheckboxTargets.forEach(cb => { cb.checked = checked })
    const hidden = checked ? new Set() : new Set(this.colVisCheckboxTargets.map(cb => cb.value))
    this._applyColVisibility(hidden)
    this._saveColVisibility([...hidden])
  }

  _applyColVisibility(hiddenKeys) {
    if (!this.hasTableTarget) return

    // 숨길 열 인덱스 집합
    const hiddenIndices = new Set()
    hiddenKeys.forEach(key => {
      if (key.startsWith('col-')) {
        hiddenIndices.add(parseInt(key.slice(4)))
      } else if (key.startsWith('phase-')) {
        const pi = parseInt(key.slice(6))
        for (let si = 0; si < 4; si++) hiddenIndices.add(9 + pi * 4 + si)
      }
    })

    // 숨길 단계 그룹 (4개 서브컬럼 모두 숨김인 경우)
    const hiddenPhases = new Set()
    hiddenKeys.forEach(key => {
      if (key.startsWith('phase-')) hiddenPhases.add(parseInt(key.slice(6)))
    })

    // 동적 <style> 엘리먼트로 CSS 규칙 주입 (display:none 대신)
    // rowspan/colspan 구조를 깨뜨리지 않으면서 시각적으로만 숨김
    let styleEl = document.getElementById('ia-col-vis-css')
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = 'ia-col-vis-css'
      document.head.appendChild(styleEl)
    }
    const hiddenCss = 'visibility:hidden;overflow:hidden;padding:0 !important;border-left-width:0 !important;border-right-width:0 !important;'
    const cellRules = [...hiddenIndices].map(i =>
      `.ia-table [data-col-idx="${i}"]{${hiddenCss}}`
    )
    const phaseRules = [...hiddenPhases].map(pi =>
      `.ia-table [data-phase-idx="${pi}"]{${hiddenCss}}`
    )
    styleEl.textContent = [...cellRules, ...phaseRules].join('\n')

    // col 너비: 숨긴 열 = 0, 나머지 정규화 (합계 100%)
    const base = this.columnWidths ? [...this.columnWidths] : this.defaultColumnWidths()
    const masked = base.map((w, i) => hiddenIndices.has(i) ? 0 : w)
    const visibleTotal = masked.reduce((s, w) => s + w, 0)
    this.columnTargets.forEach((col, i) => {
      col.style.width = hiddenIndices.has(i)
        ? '0'
        : `${visibleTotal > 0 ? (masked[i] / visibleTotal) * 100 : 0}%`
    })

    this._recalcVisibleTableWidth(hiddenKeys)
  }

  _recalcVisibleTableWidth(hiddenKeys) {
    if (!this.hasTableTarget) return
    const phaseCount = (this.phasesValue || []).length
    if (phaseCount === 0) return // 100% width mode, no adjustment needed
    const basePx = [120, 120, 120, 120, 120, 86, 100, 104, 220]
    let totalPx = 0
    basePx.forEach((px, i) => { if (!hiddenKeys.has(`col-${i}`)) totalPx += px })
    for (let pi = 0; pi < phaseCount; pi++) {
      if (!hiddenKeys.has(`phase-${pi}`)) totalPx += 340 // 90+90+90+70
    }
    const minPx = Math.max(totalPx, 400)
    this.tableTarget.style.width = `${minPx}px`
    this.tableTarget.style.minWidth = `${minPx}px`
  }

  _colVisStorageKey() {
    return `ia-table-col-vis:${this.projectIdValue}:${this.currentTabIdValue}`
  }

  _saveColVisibility(hiddenArray) {
    try { window.localStorage.setItem(this._colVisStorageKey(), JSON.stringify(hiddenArray)) } catch (_) {}
  }

  _restoreColVisibility() {
    if (!this.hasColVisCheckboxTarget) return
    try {
      const raw = window.localStorage.getItem(this._colVisStorageKey())
      if (!raw) return
      const hidden = JSON.parse(raw)
      if (!Array.isArray(hidden) || hidden.length === 0) return
      const hiddenSet = new Set(hidden)
      this.colVisCheckboxTargets.forEach(cb => { if (hiddenSet.has(cb.value)) cb.checked = false })
      const anyUnchecked = this.colVisCheckboxTargets.some(cb => !cb.checked)
      const anyChecked = this.colVisCheckboxTargets.some(cb => cb.checked)
      this.colVisAllTarget.checked = !anyUnchecked
      this.colVisAllTarget.indeterminate = anyUnchecked && anyChecked
      this._applyColVisibility(hiddenSet)
    } catch (_) {}
  }

  saveColumnWidths(widths) {
    try {
      window.localStorage.setItem(this.storageKey(), JSON.stringify(widths))
    } catch (_error) {
      // Ignore storage failures and keep the current in-memory widths.
    }
  }

  loadColumnWidths() {
    try {
      const rawValue = window.localStorage.getItem(this.storageKey())
      if (!rawValue) return null

      const widths = JSON.parse(rawValue)
      if (!Array.isArray(widths) || widths.length !== this.columnTargets.length) return null

      const normalized = widths.map((value) => Number(value))
      if (normalized.some((value) => !Number.isFinite(value) || value <= 0)) return null

      const total = normalized.reduce((sum, value) => sum + value, 0)
      if (!Number.isFinite(total) || total <= 0) return null

      return normalized.map((value) => (value / total) * 100)
    } catch (_error) {
      return null
    }
  }

  // ---------- Tabs ----------

  switchTab(e) {
    e.preventDefault()
    // 컨텍스트 메뉴 클릭이면 무시
    if (e.target.closest('.ia-tabs__ctx')) return

    const tabId = e.currentTarget.dataset.tabId
    if (!tabId || parseInt(tabId) === this.currentTabIdValue) return

    const url = new URL(window.location)
    url.searchParams.set('tab_id', tabId)
    window.location.href = url.toString()
  }

  addTab() {
    const name = prompt(this.promptNewTabValue || "새 탭 이름을 입력하세요:")
    if (!name || !name.trim()) return

    fetch(`/ia/projects/${this.projectIdValue}/tabs`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
      body: JSON.stringify({ tab: { name: name.trim() } })
    })
    .then(r => r.json())
    .then(data => {
      if (data.error) { alert(data.error); return }
      // 새 탭으로 이동
      const url = new URL(window.location)
      url.searchParams.set('tab_id', data.data.id)
      window.location.href = url.toString()
    })
  }

  editTabName(e) {
    e.preventDefault()
    e.stopPropagation()
    const item = e.currentTarget
    const tabId = item.dataset.tabId
    const nameSpan = item.querySelector('.ia-tabs__name')
    if (!nameSpan || nameSpan.querySelector('input')) return

    const currentName = item.dataset.tabName || nameSpan.textContent.trim()
    const input = document.createElement('input')
    input.type = 'text'
    input.value = currentName
    input.style.cssText = 'font-size:13px;font-weight:700;font-family:inherit;border:1px solid #6366F1;padding:2px 6px;width:' + Math.max(60, currentName.length * 14) + 'px;outline:none;background:#fff;'

    nameSpan.textContent = ''
    nameSpan.appendChild(input)
    input.focus()
    input.select()

    const save = () => {
      const newName = input.value.trim()
      if (newName && newName !== currentName) {
        fetch(`/ia/projects/${this.projectIdValue}/tabs/${tabId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
          body: JSON.stringify({ tab: { name: newName } })
        }).then(r => r.json()).then(data => {
          if (data.error) { nameSpan.textContent = currentName; return }
          nameSpan.textContent = newName
          item.dataset.tabName = newName
        })
      } else {
        nameSpan.textContent = currentName
      }
    }

    input.addEventListener('blur', save, { once: true })
    input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') { ev.preventDefault(); input.blur() }
      if (ev.key === 'Escape') { input.value = currentName; input.blur() }
    })
  }

  showTabContext(e) {
    e.preventDefault()
    // 다른 열린 메뉴 닫기 (이미 등록된 핸들러도 제거)
    document.querySelectorAll('.ia-tabs__ctx--open').forEach(el => {
      el.classList.remove('ia-tabs__ctx--open')
      const existingHandler = this._tabCtxOutsideHandlers.get(el)
      if (existingHandler) {
        document.removeEventListener('click', existingHandler)
        this._tabCtxOutsideHandlers.delete(el)
      }
    })

    const wrap = e.currentTarget.closest('.ia-tabs__tab-wrap')
    const ctx = wrap && wrap.querySelector('.ia-tabs__ctx')
    if (ctx) {
      ctx.classList.add('ia-tabs__ctx--open')
      // 외부 클릭 시 닫기
      const close = (ev) => {
        if (!ctx.contains(ev.target)) {
          ctx.classList.remove('ia-tabs__ctx--open')
          document.removeEventListener('click', close)
          this._tabCtxOutsideHandlers.delete(ctx)
        }
      }
      this._tabCtxOutsideHandlers.set(ctx, close)
      setTimeout(() => document.addEventListener('click', close), 0)
    }
  }

  renameTab(e) {
    e.stopPropagation()
    const tabId = e.currentTarget.dataset.tabId
    const btn = document.querySelector(`[data-tab-id="${tabId}"].ia-tabs__item`)
    const currentName = btn?.dataset.tabName || ""
    const name = prompt(this.promptRenameTabValue || "탭 이름:", currentName)
    if (!name || !name.trim() || name.trim() === currentName) return

    fetch(`/ia/projects/${this.projectIdValue}/tabs/${tabId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
      body: JSON.stringify({ tab: { name: name.trim() } })
    })
    .then(r => r.json())
    .then(data => {
      if (data.error) { alert(data.error); return }
      window.location.reload()
    })
  }

  deleteTab(e) {
    e.stopPropagation()
    const tabId = e.currentTarget.dataset.tabId
    if (!confirm(this.confirmDeleteTabValue || "이 탭과 포함된 모든 항목을 삭제하시겠습니까?")) return

    fetch(`/ia/projects/${this.projectIdValue}/tabs/${tabId}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() }
    })
    .then(r => r.json())
    .then(data => {
      if (data.error) { alert(data.error); return }
      // 첫 번째 탭으로 이동
      const url = new URL(window.location)
      url.searchParams.delete('tab_id')
      window.location.href = url.toString()
    })
  }

  // ---------- Planning status ----------

  cyclePlanningStatus(event) {
    const td = event.currentTarget
    const nodeId = td.dataset.cellNodeId
    const field = td.dataset.statusField || "planning_status"
    const current = td.dataset.planningStatus
    const cycle = { pending: "in_progress", in_progress: "done", done: "pending" }
    const next = cycle[current] || "pending"
    const labels = {
      pending: { label: this.statusPendingValue || "진행전", bg: "#F3F4F6", color: "#6B7280" },
      in_progress: { label: this.statusInProgressValue || "진행중", bg: "#DBEAFE", color: "#2563EB" },
      done: { label: this.statusDoneValue || "완료", bg: "#DCFCE7", color: "#16A34A" }
    }

    td.dataset.planningStatus = next
    const span = td.querySelector("span")
    const style = labels[next]
    span.textContent = style.label
    span.style.background = style.bg
    span.style.color = style.color

    const projectId = this.projectIdValue
    fetch(`/ia/projects/${projectId}/nodes/${nodeId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this._csrfToken()
      },
      body: JSON.stringify({ node: { [field]: next } })
    }).then(res => {
      if (!res.ok) {
        console.error("[IaTable] cyclePlanningStatus 서버 오류:", res.status)
        // 낙관적 갱신 롤백
        td.dataset.planningStatus = current
        const span = td.querySelector("span")
        const rollbackStyle = labels[current]
        if (span && rollbackStyle) {
          span.textContent = rollbackStyle.label
          span.style.background = rollbackStyle.bg
          span.style.color = rollbackStyle.color
        }
        alert(this.saveErrorValue || "저장 중 오류가 발생했습니다.")
      }
    }).catch(err => {
      console.error("[IaTable] cyclePlanningStatus failed:", err)
      td.dataset.planningStatus = current
      const span = td.querySelector("span")
      const rollbackStyle = labels[current]
      if (span && rollbackStyle) {
        span.textContent = rollbackStyle.label
        span.style.background = rollbackStyle.bg
        span.style.color = rollbackStyle.color
      }
      alert(this.saveErrorValue || "저장 중 오류가 발생했습니다.")
    })
  }


  cyclePriority(event) {
    const td = event.currentTarget
    const nodeId = td.dataset.cellNodeId
    const current = td.dataset.priority || ""
    const order = ["", "1", "2", "3", "4", "5", "6", "7"]
    const idx = order.indexOf(current)
    const next = order[(idx + 1) % order.length]
    const lPriorityLabelTpl = this.priorityLabelValue || '%{n}순위'
    const makePriorityLabel = (n) => lPriorityLabelTpl.replace('%{n}', n)
    const config = {
      "":  { label: "-",                   cls: "ia-table__priority--empty" },
      "1": { label: makePriorityLabel("1"), cls: "ia-table__priority--1" },
      "2": { label: makePriorityLabel("2"), cls: "ia-table__priority--2" },
      "3": { label: makePriorityLabel("3"), cls: "ia-table__priority--3" },
      "4": { label: makePriorityLabel("4"), cls: "ia-table__priority--4" },
      "5": { label: makePriorityLabel("5"), cls: "ia-table__priority--5" },
      "6": { label: makePriorityLabel("6"), cls: "ia-table__priority--6" },
      "7": { label: makePriorityLabel("7"), cls: "ia-table__priority--7" },
    }

    td.dataset.priority = next
    const span = td.querySelector("span")
    if (span) {
      span.className = config[next].cls
      span.textContent = config[next].label
      span.style.cssText = "display:inline-block;padding:2px 8px;font-size:11px;font-weight:600;"
    }

    this.updateNode(nodeId, { priority: next })
  }

  _escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
